import { useRef, forwardRef, useImperativeHandle } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import DepartureArrivalSelector from '../DepartureArrivalSelector';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import { LocationType } from '../../../../models/location-type.enum';
import { TranslationService } from '../../../../services/translation.service';
import DateTimePicker from '../../../Shared/DateTimePicker';
import Summary from './components/Summary';
import { DomHelper } from '../../../../helpers/dom.helper';
import ValidationEngine from '../../../../validation/validation-engine';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import { TripLegSelectorMode } from './trip-leg-selector-mode.enum';

export interface ITripLegOresundstagState {
  value: {
    mode: TripLegSelectorMode | undefined;
    departureTime: DateTime | undefined;
    arrivalTime: DateTime | undefined;
    departureStation: string | undefined;
    arrivalStation: string | undefined;
  };
  validationInfo: ValidationInfo;
}

interface ITripLegSelectorOresundstagProps {
  id: string;
  locationTypes?: LocationType[];
  minDateTime?: DateTime;
  maxDateTime?: DateTime;
  minuteStep?: number;
  selectLegText?: string;
  onlyHistoricTrips?: boolean;
  state: ITripLegOresundstagState;
  onChange: (value: {
    mode: TripLegSelectorMode | undefined;
    departureTime: DateTime | undefined;
    arrivalTime: DateTime | undefined;
    departureStation: string | undefined;
    arrivalStation: string | undefined;
  }) => void | undefined;
  onEditClick?: () => void;
}

export default forwardRef<IValidatable, ITripLegSelectorOresundstagProps>(function TripLegSelectorOresundstag(
  {
    id,
    minDateTime = DateTime.now().startOf('day').minus({ days: 60 }),
    maxDateTime = DateTime.now().endOf('day').plus({ days: 60 }),
    minuteStep = 5,
    onlyHistoricTrips = false,
    state,
    onChange,
    onEditClick,
  }: ITripLegSelectorOresundstagProps,
  ref
) {
  // === REFS ===
  const fromToSelectorRef = useRef<IValidatable>(null!);
  const departureDateTimePickerRef = useRef<IValidatable>(null!);
  const arrivalDateTimePickerRef = useRef<IValidatable>(null!);

  const dateTimeComparisonComponent: IValidatable = {
    checkValidity(): ValidationInfo {
      const selectedDepartureArrivalValidationInfo = new ValidationInfo();
      ValidationEngine.for(selectedDepartureArrivalValidationInfo)
        .required(state.value.departureTime)
        .required(state.value.arrivalTime)
        .isAfter(state.value.arrivalTime!, state.value.departureTime!, 'delaycompensationformpage.actualarrivalisafterplanneddeparture')
        .isBefore(state.value.arrivalTime!, state.value.departureTime!.plus({ hours: 24 }), 'delaycompensationformpage.actualarrivalistofarfromplanned')
        .validate();

      const dateTimeFieldsError = selectedDepartureArrivalValidationInfo.errors.length >= 1 ? { date: new ValidationInfo('', [''], {}), time: new ValidationInfo('', [''], {}) } : undefined;

      return new ValidationInfo('', selectedDepartureArrivalValidationInfo.errors, dateTimeFieldsError);
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      const selectedDepartureArrivalValidationInfo = new ValidationInfo(
        '',
        errors
          .filter((e) => e.name.toLocaleLowerCase() === 'plannedarrivaldatetime' || e.name.toLocaleLowerCase() === 'planneddeparturedatetime')
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );

      return selectedDepartureArrivalValidationInfo;
    },
  };

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const dateTimeComparisonValidationInfo = dateTimeComparisonComponent.checkValidity();
      return new ValidationInfo('', [], {
        fromToSelector: fromToSelectorRef.current.checkValidity(),
        departureDateTimePicker: departureDateTimePickerRef.current.checkValidity(),
        arrivalDateTimePicker: new ValidationInfo('', [], dateTimeComparisonValidationInfo.children),
        dateTimeComparison: dateTimeComparisonValidationInfo,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        fromToSelector: fromToSelectorRef.current.handleServerValidationErrors(errors),
        departureDateTimePicker: departureDateTimePickerRef.current.handleServerValidationErrors(errors),
        arrivalDateTimePicker: arrivalDateTimePickerRef.current.handleServerValidationErrors(errors),
        dateTimeComparison: dateTimeComparisonComponent.handleServerValidationErrors(errors),
      });
    },
  }));

  return (
    <div className="oresundstag-trip-leg-selector">
      <div id={`${id}-form`} className={state.value.mode === TripLegSelectorMode.edit ? 'd-block' : 'd-none'}>
        <div className="mb-3 m-0">
          <DepartureArrivalSelector
            id={`${id}-from-to-selector`}
            ref={fromToSelectorRef}
            state={{
              value: { departureStation: state.value.departureStation, arrivalStation: state.value.arrivalStation },
              validationInfo: state.validationInfo.children.fromToSelector ?? new ValidationInfo(),
            }}
            placeholderText={TranslationService.get('delaycompensationformpage.station')}
            onChange={(value) => onChange({ ...state.value, departureStation: value.departureStation, arrivalStation: value.arrivalStation })}
          />
        </div>
        <div className={state.value.mode === TripLegSelectorMode.edit ? '' : 'd-none '}>
          <div className="mb-3 mb-md-4">
            <DateTimePicker
              ref={departureDateTimePickerRef}
              legend={TranslationService.get('common.selectdeparturetime')}
              showLegend={true}
              minDateTime={minDateTime}
              maxDateTime={onlyHistoricTrips ? DateTime.now() : maxDateTime}
              minuteStep={minuteStep}
              showAsteriskOnRequiredFields={true}
              state={{ value: state.value.departureTime!, validationInfo: state.validationInfo.children.departureDateTimePicker ?? new ValidationInfo() }}
              onChange={(value) => {
                const dayChange = !value.hasSame(state.value.departureTime!, 'day');
                onChange({ ...state.value, departureTime: value });
                if (dayChange) {
                  if (state.value.arrivalTime) {
                    const updatedArrivalTime = state.value.arrivalTime.set({
                      year: value.year,
                      month: value.month,
                      day: value.day,
                    });
                    onChange({
                      ...state.value,
                      departureTime: value,
                      arrivalTime: updatedArrivalTime,
                    });
                  }
                }
              }}
            />
          </div>
          <div className="mb-3 mb-md-4">
            <DateTimePicker
              ref={arrivalDateTimePickerRef}
              legend={TranslationService.get('common.selectarrivaltime')}
              showLegend={true}
              minDateTime={minDateTime}
              maxDateTime={onlyHistoricTrips ? DateTime.now() : maxDateTime}
              minuteStep={minuteStep}
              showAsteriskOnRequiredFields={true}
              state={{ value: state.value.arrivalTime!, validationInfo: state.validationInfo.children.arrivalDateTimePicker ?? new ValidationInfo() }}
              onChange={(value) => {
                onChange({ ...state.value, arrivalTime: value });
              }}
            />
            <div role="alert">
              {state.validationInfo.children.dateTimeComparison && (
                <div id="from-to-selector-error" className="invalid-feedback d-block">
                  {state.validationInfo.children.dateTimeComparison.errors.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {state.value.mode === TripLegSelectorMode.summary && state.value.arrivalStation && state.value.departureStation && state.value.departureTime && state.value.arrivalTime && (
        <Summary
          id={id}
          onEditButtonClick={() => {
            flushSync(() => {
              onEditClick?.();
            });
            DomHelper.focusOnFirstFocusableElement(document.getElementById(`${id}-form`));
          }}
          departureTime={state.value.departureTime}
          arrivalTime={state.value.arrivalTime}
          departureStation={state.value.departureStation}
          arrivalStation={state.value.arrivalStation}
        />
      )}
    </div>
  );
});

